import React, { useEffect, useRef, useState } from "react";
import swal from "sweetalert";
import { toast } from "react-toastify";
import l from "components/util/l";
import { useGet, usePost } from "seed/api";
import { useDetail, useQuery } from "seed/gql";
import { USER } from "seed/gql/queries";
import { Loading } from "seed/helpers";
import { usePrototypeDocument } from "components/brief/util/hooks/usePrototypeDocument";
import {
  buildAnnotation,
  getVisibleAnnotations,
  uploadScreenshot,
  SCREENSHOT_ANNOTATION,
} from "components/brief/util/prototypeAnnotations";
import View from "components/brief/reviewer/prototype/Prototype.view";
import PrototypeCover from "components/brief/reviewer/prototype/PrototypeCover";

const Prototype = ({
  flow,
  isDeveloper = false,
}) => {

  const userId = sessionStorage.getItem("id");

  const saveActionRef = useRef("PUBLISH");
  const frameRef = useRef(null);
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);
  const [isUploadingScreenshot, setIsUploadingScreenshot] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [selectedProposalIndex, setSelectedProposalIndex] = useState(0);
  const [editingAnnotationId, setEditingAnnotationId] = useState(null);
  const [draftText, setDraftText] = useState("");
  const [screen, setScreen] = useState("cover");

  const requestUser = useDetail(USER, userId);
  const requestRevision = useGet("/flows/get_last_revision", { flow_id: flow.id });

  const [callSaveRevisionSection, requestSaveRevisionSection] = usePost("/flows/edit_revision_proposal", {
    onCompleted: () => {
      if (saveActionRef.current === "DRAFT") {
        swal(l("Cambios guardados%PA629"), l("Se han guardado tus cambios correctamente.%PA631"), "success");
        saveActionRef.current = "PUBLISH";
        return;
      }

      swal(l("Comentarios enviados%PA630"), l("Se han enviado tus comentarios correctamente.%PA632"), "success")
        .then(() => window.location.reload());
    },
  });

  // The endpoint answers with the revision's id, not the revision. Read as `revision.id`
  // this was always undefined, the filter below went out as `version.id=undefined`, and
  // the proposals it came back with belonged to whatever flow the server felt like --
  // which is why saving them 404'd on `version__flow=flow`.
  const revisionId = requestRevision.data?.revision_id;
  const reqProposals = useQuery(`{
    flowRevisions {
      id
      name
      proposal
      prototype
      context
      solution
      status
    }
  }`, `version.id=${revisionId}`, {
    skip: !revisionId,
    onCompleted: (data) => setProposals((data.flowRevisions || []).map(toEditable)),
  });

  const selectedProposal = proposals[selectedProposalIndex] || {};
  const prototypes = selectedProposal.prototype || {};
  const prototypeFile = prototypes?.html;

  const { documentUrl, isLoadError } = usePrototypeDocument(prototypeFile);
  const annotations = prototypes.annotations || [];
  const visibleAnnotations = getVisibleAnnotations(annotations, isDeveloper);

  const user = requestUser.data?.user;
  const currentAuthor = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
  const isSaving = requestSaveRevisionSection.loading;

  const updatePrototypes = (getNextPrototypes) => {
    setProposals((currentProposals) => {
      const nextProposals = [...currentProposals];
      const currentProposal = nextProposals[selectedProposalIndex] || {};
      nextProposals[selectedProposalIndex] = {
        ...currentProposal,
        prototype: getNextPrototypes(currentProposal.prototype || {}),
      };
      return nextProposals;
    });
  };

  const updateAnnotations = (getNextAnnotations) => {
    updatePrototypes((currentPrototypes) => ({
      ...currentPrototypes,
      annotations: getNextAnnotations(currentPrototypes.annotations || []),
    }));
  };

  const onSelectAnnotation = (annotationId) => {
    const annotation = annotations.find((item) => item.id === annotationId);
    if (!annotation) return;
    setEditingAnnotationId(annotation.id);
    setDraftText(annotation.text || "");
  };

  const onClickAddScreenshotComment = () => {
    if (!frameRef.current?.contentDocument?.body) {
      toast(l("El prototipo aún no termina de cargar%BR011"), { type: "warning" });
      return;
    }
    setIsCapturingScreenshot(true);
  };

  // The comment was already written inside the overlay, so the annotation lands finished
  // and does not reopen the inline editor. A capture that failed still leaves the words,
  // which are the part the team cannot recover on its own.
  const onFinishScreenshot = async (result) => {

    setIsCapturingScreenshot(false);

    if (!result) return;

    setIsUploadingScreenshot(true);
    const screenshot = result.screenshot ? await uploadScreenshot(result.screenshot, flow.id) : null;
    setIsUploadingScreenshot(false);

    if (!screenshot)
      toast(l("No se pudo generar la captura%ES005"), { type: "warning" });

    updateAnnotations((currentAnnotations) => [
      ...currentAnnotations,
      buildAnnotation({
        author: currentAuthor,
        isDeveloper,
        screenshot,
        text: (result.annotations || "").trim(),
        type: SCREENSHOT_ANNOTATION,
      }),
    ]);

  };

  const onClickSaveEditingComment = (annotation) => {
    updateAnnotations((currentAnnotations) => currentAnnotations.map((item) =>
      item.id === annotation.id ? { ...item, text: draftText } : item
    ));
    setEditingAnnotationId(null);
    setDraftText("");
  };

  const onClickStartEditingComment = (annotation) => {
    setEditingAnnotationId(annotation.id);
    setDraftText(annotation.text || "");
  };

  const onClickCancelEditingComment = () => {
    setEditingAnnotationId(null);
    setDraftText("");
  };

  const onClickRemoveComment = (annotation) => {
    updateAnnotations((currentAnnotations) => currentAnnotations.filter((item) => item.id !== annotation.id));
    if (editingAnnotationId === annotation.id)
      onClickCancelEditingComment();
  };

  const onChangeResolvedState = (annotation, isResolved) => {
    updateAnnotations((currentAnnotations) => currentAnnotations.map((item) =>
      item.id === annotation.id ? { ...item, is_solved: isResolved } : item
    ));
  };

  // A draft is the author's own snapshot; a published change is labelled by who sent it,
  // because the team reads the revision list to tell client feedback from its own work.
  const getRevisionName = (status) => {
    if (status === "DRAFT") return "Borrador prototipo";
    if (isDeveloper) return "Cambio prototipo";
    return "[CLIENT] Cambio prototipo";
  };

  const savePrototypeComments = (status) => {
    saveActionRef.current = status === "DRAFT" ? "DRAFT" : "PUBLISH";

    callSaveRevisionSection({
      flow_id: flow.id,
      proposal_id: selectedProposal.id,
      target: "prototype",
      data: prototypes,
      revision_name: getRevisionName(status),
      revision_type: isDeveloper ? "PRODUCT" : "CLIENT",
      priority: isDeveloper ? "LOW" : "HIGHEST",
      status,
    });
  };

  const onClickSendComments = async () => {
    const confirmed = await swal({
      title: l("¿Enviar comentarios?%PA634"),
      text: l("Si quieres seguir comentando, guarda cambios. Al enviar, tus comentarios se publicarán para el equipo.%PA635"),
      icon: "warning",
      buttons: {
        cancel: l("Cancelar%GA021"),
        confirm: { text: l("Enviar comentarios%PA610"), value: true },
      },
    });

    if (!confirmed) return;

    savePrototypeComments("PUBLISHED");
  };

  useEffect(() => {
    if (selectedProposalIndex >= proposals.length)
      setSelectedProposalIndex(0);
  }, [proposals.length, selectedProposalIndex]);

  if (requestRevision.loading || reqProposals.loading)
    return <Loading />;

  return (
    <div
      className="w-100 d-flex flex-column overflow-hidden m-3"
    >
      {screen === "cover" && (
        <PrototypeCover flow={flow} onClickNext={() => setScreen("view")} />
      )}

      {screen === "view" && (
        <div className="flex-fill d-flex overflow-hidden p-2 p-md-3" style={{ minHeight: 0 }}>
          <div className="w-100 h-100 rounded shadow-lg overflow-hidden d-flex animate__animated animate__fadeIn">
            <View
              documentUrl={documentUrl}
              isLoadError={isLoadError}
              flow={flow}
              annotations={visibleAnnotations}
              frameRef={frameRef}
              isCapturingScreenshot={isCapturingScreenshot}
              isUploadingScreenshot={isUploadingScreenshot}
              proposals={proposals}
              selectedProposalIndex={selectedProposalIndex}
              editingAnnotationId={editingAnnotationId}
              draftText={draftText}
              isDeveloper={isDeveloper}
              isSaving={isSaving}
              hasPrototype={!!prototypeFile}
              onSelectAnnotation={onSelectAnnotation}
              onClickSaveChanges={() => savePrototypeComments("DRAFT")}
              onChangeSelectedProposalIndex={setSelectedProposalIndex}
              onClickStartEditingComment={onClickStartEditingComment}
              onClickSaveEditingComment={onClickSaveEditingComment}
              onClickCancelEditingComment={onClickCancelEditingComment}
              onClickRemoveComment={onClickRemoveComment}
              onClickAddScreenshotComment={onClickAddScreenshotComment}
              onFinishScreenshot={onFinishScreenshot}
              onChangeDraftText={(event) => setDraftText(event.target.value)}
              onChangeResolvedState={onChangeResolvedState}
              onClickSendComments={onClickSendComments}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const toEditable = (proposal) => ({
  id: proposal.id,
  name: proposal.name,
  status: proposal.status,
  context: proposal.context || {},
  prototype: proposal.prototype || {},
  solution: proposal.solution || {},
  type: proposal.proposal?.type || "major",
  reference: proposal.proposal?.reference || null,
  content: proposal.proposal?.content || [],
  additional_comments: proposal.proposal?.additional_comments || "",
});

export default Prototype;
